import client from '../../shared/api/client';
import {
  FinanceCategory,
  FinanceOperation,
  FinancePlatformSummary,
  FinanceSummary,
  PaginatedResponse,
} from '../../shared/types';

interface FinanceBackendResponse<T> {
  success?: boolean;
  data: T;
}

export interface OperationFilters {
  page?: number;
  per_page?: number;
  user_id?: string | number;
  wallet_id?: number;
  currency?: string;
  category?: FinanceCategory | '';
  reference_id?: string;
  date_from?: string;
  date_to?: string;
}

const toParams = (filters: OperationFilters): Record<string, string | number | undefined> => {
  const params: Record<string, string | number | undefined> = {};
  if (filters.user_id) params.user_id = filters.user_id;
  if (filters.wallet_id) params.wallet_id = filters.wallet_id;
  if (filters.currency) params.currency = filters.currency;
  if (filters.category) params.category = filters.category;
  if (filters.reference_id) params.reference_id = filters.reference_id;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;
  return params;
};

export const listOperations = async (
  filters: OperationFilters = {}
): Promise<PaginatedResponse<FinanceOperation>> => {
  const params = {
    page: filters.page ?? 1,
    per_page: filters.per_page ?? 20,
    ...toParams(filters),
  };

  const response = await client.get<FinanceBackendResponse<PaginatedResponse<FinanceOperation>>>(
    '/finance/operations',
    { params }
  );
  return response.data.data;
};

export const getOperationsSummary = async (
  filters: OperationFilters = {}
): Promise<FinanceSummary> => {
  const response = await client.get<FinanceBackendResponse<FinanceSummary>>(
    '/finance/operations/summary',
    { params: toParams(filters) }
  );
  return response.data.data;
};

export interface PlatformRateRow {
  currency: string;
  binance: number | string | null;
  bybit: number | string | null;
  okx: number | string | null;
  average: number | string | null;
}

export interface FiatRateRow {
  currency: string;
  rate: number | string | null;
}

export interface PlatformRates {
  formula: string;
  source: string;
  sources_used: string[];
  fetched_at: string | null;
  rates: PlatformRateRow[];
  fiat_rates?: FiatRateRow[];
  fiat_source?: string;
  fiat_fetched_at?: string | null;
}

export const getPlatformRates = async (refresh = false): Promise<PlatformRates> => {
  const response = await client.get<FinanceBackendResponse<PlatformRates>>(
    '/finance/platform-rates',
    { params: refresh ? { refresh: 1 } : {} }
  );
  return response.data.data;
};

export interface PairRateLeg {
  currency: string;
  type: 'crypto' | 'fiat';
  usd: number;
  source: string | null;
  fetched_at: string | null;
}

export interface PlatformPairRate {
  base: string;
  quote: string;
  pair: string;
  rate: number;
  inverse_rate: number;
  base_leg: PairRateLeg;
  quote_leg: PairRateLeg;
  crypto_source: string | null;
  crypto_sources_used: string[];
  crypto_fetched_at: string | null;
  fiat_source: string | null;
  fiat_fetched_at: string | null;
  supported: { crypto: string[]; fiat: string[] };
}

export const getPlatformPairRate = async (
  base: string,
  quote: string,
  refresh = false
): Promise<PlatformPairRate> => {
  const response = await client.get<FinanceBackendResponse<PlatformPairRate>>(
    '/finance/platform-rates/pair',
    { params: { base, quote, ...(refresh ? { refresh: 1 } : {}) } }
  );
  return response.data.data;
};

export interface PlatformAccountRow {
  id: number;
  user_id: number;
  name: string;
  is_active: boolean;
  activated_at: string | null;
  deactivated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface PlatformAccountUser {
  id: number | null;
  name: string | null;
  email: string | null;
  type: string | null;
  is_active: boolean | null;
  created_at?: string | null;
}

export interface PlatformAccountInfo {
  account: PlatformAccountRow | null;
  user?: PlatformAccountUser;
  active_user_id: number | null;
}

export const getPlatformAccount = async (): Promise<PlatformAccountInfo> => {
  const response = await client.get<FinanceBackendResponse<PlatformAccountInfo>>(
    '/finance/platform-account'
  );
  return response.data.data;
};

export const createPlatformAccount = async (payload: {
  name: string;
  email?: string;
}): Promise<{ account: PlatformAccountRow | null; user: PlatformAccountUser }> => {
  const response = await client.post<
    FinanceBackendResponse<{ account: PlatformAccountRow | null; user: PlatformAccountUser }>
  >('/finance/platform-account', payload);
  return response.data.data;
};

export const deactivatePlatformAccount = async (): Promise<PlatformAccountRow | null> => {
  const response = await client.post<FinanceBackendResponse<{ account: PlatformAccountRow | null }>>(
    '/finance/platform-account/deactivate'
  );
  return response.data.data.account;
};

export const activatePlatformAccount = async (id: number): Promise<PlatformAccountRow | null> => {
  const response = await client.post<FinanceBackendResponse<{ account: PlatformAccountRow | null }>>(
    `/finance/platform-account/${id}/activate`
  );
  return response.data.data.account;
};

export const getPlatformAccountHistory = async (): Promise<PlatformAccountRow[]> => {
  const response = await client.get<FinanceBackendResponse<{ accounts: PlatformAccountRow[] }>>(
    '/finance/platform-account/history'
  );
  return response.data.data.accounts;
};

export const getPlatformSummary = async (
  filters: Pick<OperationFilters, 'currency' | 'date_from' | 'date_to'> = {}
): Promise<FinancePlatformSummary> => {
  const params: Record<string, string | undefined> = {};
  if (filters.currency) params.currency = filters.currency;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;

  const response = await client.get<FinanceBackendResponse<FinancePlatformSummary>>(
    '/finance/platform-summary',
    { params }
  );
  return response.data.data;
};
